import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, CalendarDays, Eye, MessageSquare, ChevronRight, Loader2 } from 'lucide-react';
import dayjs from 'dayjs';
import { api } from '../apiClient';

const STATUSES = ['all', 'draft', 'live', 'completed', 'cancelled'];
const STATUS_STYLE = {
  draft: { bg: 'bg-amber-50', fg: 'text-amber-600' },
  submitted: { bg: 'bg-pink-50', fg: 'text-pink-700' },
  live: { bg: 'bg-emerald-50', fg: 'text-emerald-600' },
  completed: { bg: 'bg-pink-50', fg: 'text-pink-700' },
  cancelled: { bg: 'bg-red-50', fg: 'text-red-500' },
  postponed: { bg: 'bg-amber-50', fg: 'text-amber-600' },
};

export default function OrgEventsPage() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const params = filter === 'all' ? {} : { status: filter };
      const r = await api.get('/organizer/events', { params });
      setEvents(r.data);
    } catch {
      setEvents([]);
    }
  }, [filter]);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [load]);

  return (
    <div className="max-w-5xl mx-auto px-6" data-testid="org-events-page">
      <div className="flex items-center justify-between py-4">
        <h1 className="text-2xl font-black text-gray-900 tracking-tight">My Events</h1>
        <button
          data-testid="events-create-btn"
          onClick={() => navigate('/organizer/events/new')}
          className="flex items-center gap-1 bg-brand-magenta hover:bg-pink-700 text-white px-4 py-2.5 rounded-full text-sm font-extrabold transition-colors"
        >
          <Plus className="w-4 h-4" /> New
        </button>
      </div>

      <div className="flex gap-2 pb-3 overflow-x-auto no-scrollbar">
        {STATUSES.map((s) => (
          <button
            key={s}
            data-testid={`events-filter-${s}`}
            onClick={() => setFilter(s)}
            className={`flex-shrink-0 px-4 py-2 rounded-full text-xs font-bold border transition-colors ${
              filter === s
                ? 'bg-brand-magenta border-brand-magenta text-white'
                : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
            }`}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-brand-magenta" /></div>
      ) : events.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20" data-testid="events-empty">
          <CalendarDays className="w-16 h-16 text-gray-300 mb-4" />
          <h3 className="text-lg font-extrabold text-gray-900">{filter === 'all' ? 'No events yet' : `No ${filter} events`}</h3>
          <p className="text-sm text-gray-500 mt-1 text-center">Create your first event to start collecting enquiries.</p>
          <button onClick={() => navigate('/organizer/events/new')} className="mt-5 bg-brand-magenta hover:bg-pink-700 text-white px-6 py-3 rounded-2xl font-extrabold text-sm transition-colors">
            Create Event
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-3 pb-8">
          {events.map((item) => {
            const st = STATUS_STYLE[item.status || 'live'] || STATUS_STYLE.live;
            return (
              <button
                key={item.id}
                data-testid={`org-event-${item.id}`}
                onClick={() => navigate(`/organizer/events/${item.id}`)}
                className="flex items-center gap-3 bg-white rounded-2xl border border-gray-100 shadow-sm p-3 text-left hover:bg-gray-50 transition-colors"
              >
                <img src={item.banner_url} alt="" className="w-[72px] h-[72px] rounded-xl object-cover bg-gray-100 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`${st.bg} ${st.fg} text-[9px] font-black tracking-wide px-2 py-0.5 rounded-full`}>{(item.status || 'LIVE').toUpperCase()}</span>
                    <span className="text-xs text-gray-500">{item.category}</span>
                  </div>
                  <p className="text-[15px] font-extrabold text-gray-900 line-clamp-2 leading-snug">{item.title}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{item.city} · {dayjs(item.start_datetime).format('MMM D, h:mm A')}</p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="flex items-center gap-1 text-[11px] text-gray-500"><Eye className="w-3 h-3" />{item.view_count}</span>
                    <span className="flex items-center gap-1 text-[11px] text-gray-500"><MessageSquare className="w-3 h-3" />{item.enquiry_count}</span>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
